import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

import pageMeta from './page-meta.json';

interface PageMeta {
  title: string;
  description: string;
  noindex?: boolean;
}

const META = pageMeta as Record<string, PageMeta>;
const CANONICAL_ORIGIN = 'https://sunsetharborcb.com';

function normalize(pathname: string): string {
  const trimmed = pathname.replace(/\/+$/, '');
  return trimmed === '' ? '/' : trimmed;
}

function upsertMeta(attr: 'name' | 'property', key: string, content: string): void {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function upsertCanonical(href: string): void {
  let el = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', 'canonical');
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

/**
 * Keeps the document head in sync with the current route for client-side
 * navigation. The same `page-meta.json` drives the build-time prerender
 * (apps/web/prerender.mjs), so crawlers and SPA users see identical heads.
 */
export function usePageMeta(): void {
  const { pathname } = useLocation();

  useEffect(() => {
    const key = normalize(pathname);
    const meta = META[key] ?? META['/'];
    if (!meta) return;

    const canonical = `${CANONICAL_ORIGIN}${key === '/' ? '/' : key}`;
    document.title = meta.title;
    upsertMeta('name', 'description', meta.description);
    upsertMeta('property', 'og:title', meta.title);
    upsertMeta('property', 'og:description', meta.description);
    upsertMeta('property', 'og:url', canonical);
    upsertCanonical(canonical);
  }, [pathname]);
}
